<script setup>
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { LCircle, LCircleMarker, LMap, LTileLayer, LPolyline } from '@vue-leaflet/vue-leaflet'
import { useGeocodeStore } from '@/stores/geocode.js'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'

import MapServiceProviderPanel from '@/components/map/map_service_provider_panel.vue'

const props = defineProps({
  centerOverride: {
    type: Object,
    default: null
  },
  showUserLocation: {
    type: Boolean,
    default: false
  },
  searchLocation: {
    type: Object
  },
  searchRadius: {
    type: Number, default: 0
  },
  defaultZoom: {
    type: Number,
    default: 11
  },
  showCenterMarker: {
    type: Boolean,
    default: false
  },
  showConnectionLine: {
    type: Boolean,
    default: false
  },
  disableInteractions: {
    type: Boolean,
    default: false
  }
})

// I18n
const { t } = useI18n()

// Stores
const geocodeStore = useGeocodeStore()

// Variables
const selectedServiceProvider = ref(null)
const isMapLoading = ref(true)
const userLatLng = computed(() => {
  const location = props.searchLocation && isValidLocation(props.searchLocation) ? props.searchLocation : geocodeStore.userLocation
  return [location.latitude || 0, location.longitude || 0]
})
const center = computed(() => {
  if (props.centerOverride) {
    return props.centerOverride
  } else if (props.searchLocation && isValidLocation(props.searchLocation)) {
    return [props.searchLocation.latitude, props.searchLocation.longitude]
  } else if (isValidLocation(geocodeStore.userLocation)) {
    return userLatLng.value
  } else {
    return null
  }
})
const tileAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
const mapRef = ref()
const circleRef = ref()
const zoomLevel = ref(props.defaultZoom)

onMounted(async () => {
  setTimeout(() => {
    isMapLoading.value = false
  }, 500)
})

watch(() => [props.searchLocation, props.searchRadius], () => {
  isMapLoading.value = true
  setTimeout(() => {
    isMapLoading.value = false
  }, 300)
})

// Query Service Providers
const serviceProviderUrl = computed(() => {
  if (!center.value) return
  if (!props.searchLocation) return

  const url = new URL(`${import.meta.env.VITE_BACKEND_URL}/api/v2/service_providers/nearby`)
  url.searchParams.append('latitude', center.value[0])
  url.searchParams.append('longitude', center.value[1])
  url.searchParams.append('radius', props.searchRadius)
  return url.href
})
const {
  isFetching: isServiceProvidersLoading,
  data: serviceProvidersData
} = useIntelichekFetch(serviceProviderUrl, { refetch: true }).json()

function isValidLocation(location) {
  return !!(location.address
    && location.city
    && location.state
    && location.country
    && location.latitude !== 0
    && location.longitude !== 0)
}

function setServiceProvider(event, serviceProvider) {
  serviceProvider.state = serviceProvidersData.value.included
    .find(x => x.type === 'us_state'
      && Number(x.id) === Number(serviceProvider.attributes.state_id))
  selectedServiceProvider.value = serviceProvider
}

watchDebounced(
  () => props.searchRadius,
  () => {
    if (!mapRef.value?.leafletObject) return props.defaultZoom
    if (!circleRef.value?.leafletObject) return props.defaultZoom

    const bounds = circleRef.value.leafletObject.getBounds()
    zoomLevel.value = mapRef.value.leafletObject.getBoundsZoom(bounds)
  },
  { debounce: 100 })

</script>

<template>
  <div class="h-full w-full">
    <div class="h-full w-full relative" v-if="center">
      <!-- Loading Overlay -->
      <div v-if="isMapLoading" class="absolute inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
        <div class="text-center">
          <div class="animate-spin inline-block size-16 border-4 border-current border-t-transparent text-[#386196] rounded-full" role="status" :aria-label="t('map.loading')">
            <span class="sr-only">{{ t('map.loading') }}</span>
          </div>
          <p class="text-lg mt-3 text-[#386196] font-medium">{{ t('request_a_quote.location_section.loading_map') }}</p>
        </div>
      </div>
      <div class="h-full w-full z-1">
        <l-map ref="mapRef" :zoom="zoomLevel" :center="center" :use-global-leaflet="false"
               :options="props.disableInteractions ? { zoomControl: false, dragging: false, attributionControl: false, scrollWheelZoom: false } : {}">
          <!--      <l-marker :lat-lng="center"></l-marker>-->

          <l-circle-marker v-if="props.showUserLocation"
                           :lat-lng="userLatLng"
                           :radius="8"
                           :fill-opacity="1"
                           fill-color="#E8834A"
                           :stroke="false"
                           :interactive="false" />

          <l-circle-marker v-if="center && props.showCenterMarker"
                           :lat-lng="center"
                           :radius="8"
                           :fill-opacity="1"
                           fill-color="#29476b"
                           :stroke="false"
                           :interactive="false" />

          <l-polyline v-if="props.showConnectionLine && props.showUserLocation && center && userLatLng[0] && userLatLng[1] && center[0] && center[1]"
                      :lat-lngs="[[userLatLng[0], userLatLng[1]], [center[0], center[1]]]"
                      color="#E8834A"
                      :weight="3"
                      :opacity="0.7"
                      :dash-array="[5, 10]"
                      :interactive="false" />

          <l-circle-marker v-if="serviceProvidersData"
                           v-for="serviceProvider in serviceProvidersData.data"
                           :key="serviceProvider.id"
                           :lat-lng="serviceProvider.attributes.latlng"
                           :radius="8"
                           :color="selectedServiceProvider?.id === serviceProvider.id ? '#FF0000' : '#3388ff'"
                           :fill-color="selectedServiceProvider?.id === serviceProvider.id ? '#FF0000' : '#3388ff'"
                           @click="(e) => setServiceProvider(e, serviceProvider)" />

          <l-circle ref="circleRef" v-if="searchRadius" :lat-lng="center"
                    :radius="props.searchRadius * 1609.34"
                    color="#FF9D6E"
                    :interactive="false"></l-circle>

          <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        layer-type="base"
                        name="OpenStreetMap"
                        :attribution="tileAttribution"></l-tile-layer>
        </l-map>
      </div>
      <!-- Desktop drawer -->
      <div class="hidden lg:block w-80 bg-white h-full transition-transform duration-300 ease-in-out top-0 right-0 absolute z-50"
           :class="selectedServiceProvider ? 'translate-x-0 shadow-lg' : 'translate-x-full'">
        <button @click="selectedServiceProvider = null" class="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:bg-gray-100 text-gray-800 font-bold z-10">
          ✕
        </button>
        <div v-if="selectedServiceProvider && isServiceProvidersLoading" class="h-full flex flex-col">
          <div class="flex-none w-full h-24 bg-gray-200 animate-pulse rounded-tr-lg"></div>
          <div class="p-8 grow flex flex-col gap-4">
            <div class="h-8 bg-gray-200 animate-pulse rounded w-3/4"></div>
            <div class="h-4 bg-gray-200 animate-pulse rounded w-full"></div>
            <div class="h-4 bg-gray-200 animate-pulse rounded w-2/3"></div>
            <div class="h-4 bg-gray-200 animate-pulse rounded w-1/2"></div>
            <div class="h-4 bg-gray-200 animate-pulse rounded w-3/4"></div>
          </div>
        </div>
        <MapServiceProviderPanel v-else-if="selectedServiceProvider" :service-provider="selectedServiceProvider"></MapServiceProviderPanel>
      </div>

      <!-- Mobile pin popup -->
      <div class="lg:hidden absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg p-3 z-50 transition-all duration-300 ease-in-out"
           :class="selectedServiceProvider ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'">
        <div v-if="selectedServiceProvider">
          <div class="flex justify-between items-center">
            <h3 class="font-semibold text-sm">{{ selectedServiceProvider.attributes.name }}</h3>
            <button @click="selectedServiceProvider = null" class="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="w-full h-full flex items-center justify-center flex-col">
      <div>
        <div
          class="animate-spin inline-block size-24 border-4 border-current border-t-transparent text-[#386196] rounded-full"
          role="status" :aria-label="t('map.loading')">
          <span class="sr-only">{{ t('map.loading') }}</span>
        </div>
        <p class="text-2xl mt-4 text-[#3a358c]">{{ t('map.loading') }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.leaflet-container {
  z-index: 1 !important;
}
</style>
